/**
 * Offline queue for the guided workout. Logging, undoing and rating a set is
 * stored on the phone first and sent to the API in order; without a connection
 * the entries wait and are retried. Sets get their id here, so sending one
 * again after a lost response doesn't store it twice (the API accepts the id).
 *
 * Entries stay (marked as synced) until the workout is finished or discarded,
 * so the trainer can rebuild its state after a reload even when the training
 * data comes from the service worker's cache and is older than the last sets.
 */
import { useSyncExternalStore } from "react";
import { api, ApiError, call } from "./api";

export type SetValues = {
  workoutExerciseId: string | null;
  exerciseId: string;
  setIndex: number;
  isWarmup: boolean;
  weight: number | null;
  reps: number | null;
  durationSec: number | null;
  distanceKm: number | null;
};

type Op =
  | { kind: "create"; id: string; sessionId: string; set: SetValues }
  | { kind: "rir"; id: string; sessionId: string; rir: number | null }
  | { kind: "delete"; id: string; sessionId: string };

type Entry = Op & { key: string; synced: boolean; at: number };

/** The shape of a logged set in the trainer. */
export type QueuedSet = SetValues & { id: string; rir: number | null };

const STORAGE_KEY = "liftquest.outbox.v1";
/** Synced entries of workouts that were never finished are dropped after this. */
const KEEP_SYNCED_MS = 7 * 24 * 3600_000;
const RETRY_MS = [3_000, 10_000, 30_000, 60_000];

let entries: Entry[] = load();
const listeners = new Set<() => void>();
const rejectedListeners = new Set<(op: Op) => void>();
let flushing: Promise<boolean> | null = null;
let inflight: string | null = null;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let retries = 0;

function load(): Entry[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "[]") as Entry[];
    const cutoff = Date.now() - KEEP_SYNCED_MS;
    return Array.isArray(parsed) ? parsed.filter((e) => !e.synced || e.at > cutoff) : [];
  } catch {
    return [];
  }
}

function changed() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // storage full or blocked: the queue still works for this page
  }
  listeners.forEach((l) => l());
}

/** 21 URL-safe random characters (like nanoid); works without a secure context, unlike randomUUID. */
export function newSetId() {
  const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-";
  return Array.from(crypto.getRandomValues(new Uint8Array(21)), (b) => alphabet[b & 63]).join("");
}

let keyCounter = 0;
const entry = (op: Op): Entry => ({ ...op, key: `${Date.now()}-${keyCounter++}`, synced: false, at: Date.now() });

export function logSet(sessionId: string, id: string, set: SetValues) {
  entries.push(entry({ kind: "create", id, sessionId, set }));
  changed();
  void flush();
}

export function setRir(sessionId: string, id: string, rir: number | null) {
  const queued = entries.find((e) => e.kind === "rir" && e.id === id && !e.synced);
  if (queued?.kind === "rir" && queued.key !== inflight) queued.rir = rir;
  else entries.push(entry({ kind: "rir", id, sessionId, rir }));
  changed();
  void flush();
}

export function undoSet(sessionId: string, id: string) {
  const create = entries.find((e) => e.kind === "create" && e.id === id);
  if (create && !create.synced && create.key !== inflight) {
    // Never reached the server: forget it instead of sending it and deleting it again.
    entries = entries.filter((e) => e.id !== id);
  } else {
    entries = entries.filter((e) => !(e.kind === "rir" && e.id === id && !e.synced && e.key !== inflight));
    entries.push(entry({ kind: "delete", id, sessionId }));
  }
  changed();
  void flush();
}

/** Forget everything about a workout (after it was finished or discarded). */
export function clearSession(sessionId: string) {
  entries = entries.filter((e) => e.sessionId !== sessionId || e.key === inflight);
  changed();
}

/** Forget everything (sign-out). */
export function clearAll() {
  entries = [];
  changed();
}

export function pendingCount(sessionId: string) {
  return entries.filter((e) => e.sessionId === sessionId && !e.synced).length;
}

/** The trainer's sets: what the server returned plus what this phone logged, undid or rated since. */
export function mergeQueued<T extends QueuedSet>(server: T[], sessionId: string): QueuedSet[] {
  const mine = entries.filter((e) => e.sessionId === sessionId);
  const deleted = new Set(mine.filter((e) => e.kind === "delete").map((e) => e.id));
  const rir = new Map(mine.flatMap((e) => (e.kind === "rir" ? [[e.id, e.rir] as const] : [])));
  const result: QueuedSet[] = server.filter((s) => !deleted.has(s.id));
  for (const e of mine) {
    if (e.kind === "create" && !deleted.has(e.id) && !result.some((s) => s.id === e.id)) {
      result.push({ ...e.set, id: e.id, rir: null });
    }
  }
  return result.map((s) => (rir.has(s.id) ? { ...s, rir: rir.get(s.id)! } : s));
}

/** Called when the API refuses an entry for good (e.g. the workout was finished on another device). */
export function onRejected(fn: (op: Op) => void) {
  rejectedListeners.add(fn);
  return () => {
    rejectedListeners.delete(fn);
  };
}

export function usePendingCount(sessionId: string) {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => pendingCount(sessionId),
  );
}

function send(e: Entry) {
  switch (e.kind) {
    case "create":
      return call(api.sessions[":id"].sets.$post({ param: { id: e.sessionId }, json: { id: e.id, ...e.set } }));
    case "rir":
      return call(api.sets[":id"].$patch({ param: { id: e.id }, json: { rir: e.rir } }));
    case "delete":
      return call(api.sets[":id"].$delete({ param: { id: e.id } }));
  }
}

/** Errors that sending again can't fix. 401 (signed out), 408 and 429 are worth retrying. */
function isPermanent(err: unknown) {
  return err instanceof ApiError && err.status >= 400 && err.status < 500 && ![401, 408, 429].includes(err.status);
}

/**
 * Sends the waiting entries in order. Resolves to true when nothing is left;
 * on a network or server error it stops and tries again later.
 */
export function flush(): Promise<boolean> {
  if (!flushing) {
    const run = sendQueued();
    flushing = run;
    // Cleared once the run has settled, never inside it: with nothing to send, sendQueued()
    // has already finished when it returns, so clearing it there would leave this settled
    // promise behind and every later flush() would return it without sending anything.
    void run.then(
      (done) => {
        flushing = null;
        // Queued after this run last looked at the queue: send it too.
        if (done && entries.some((e) => !e.synced)) void flush();
      },
      () => {
        flushing = null;
      },
    );
  }
  return flushing;
}

async function sendQueued(): Promise<boolean> {
  for (;;) {
    const next = entries.find((e) => !e.synced);
    if (!next) {
      retries = 0;
      return true;
    }
    inflight = next.key;
    try {
      await send(next);
      next.synced = true;
    } catch (err) {
      const gone = err instanceof ApiError && err.status === 404 && next.kind === "delete";
      if (gone) {
        next.synced = true;
      } else if (isPermanent(err)) {
        // Drop it, and with a refused set everything that refers to it.
        entries = entries.filter((e) => e.key !== next.key && !(next.kind === "create" && e.id === next.id));
        rejectedListeners.forEach((l) => l(next));
      } else {
        scheduleRetry();
        return false;
      }
    } finally {
      inflight = null;
      changed();
    }
  }
}

function scheduleRetry() {
  clearTimeout(retryTimer);
  retryTimer = setTimeout(() => void flush(), RETRY_MS[Math.min(retries++, RETRY_MS.length - 1)]);
}

if (typeof window !== "undefined") {
  window.addEventListener("online", () => void flush());
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void flush();
  });
  // Another tab changed the queue.
  window.addEventListener("storage", (e) => {
    if (e.key !== STORAGE_KEY) return;
    entries = load();
    listeners.forEach((l) => l());
  });
  if (entries.some((e) => !e.synced)) void flush();
}
